<template>
  <div>
    <div class="row">
      <div class="col-sm-2">
        <OverviewBox title="Stocktake" :loading="loading" avatarImage="/glass/img/stocktake-overview-1.png" :avatarCircleMode="false">
          <h4 v-show="stocktake.name">{{stocktake.name}}</h4>
          <OverviewBoxList>
            <OverviewBoxListItem title="Status">
              <LabelBadge :labelClass="`label-stocktake-status-${stocktake.status}`">{{stocktake.status | formatLookupName(StocktakeStatusTypeLookup)}}</LabelBadge>
            </OverviewBoxListItem>
            <OverviewBoxListItem title="Created By">
              <LinkUser v-if="stocktake.user" :id="stocktake.userId" :name="stocktake.user.name" />
            </OverviewBoxListItem>
            <OverviewBoxListItem title="Created On">{{stocktake.createdAt | formatDate}}</OverviewBoxListItem>
            <OverviewBoxListItem title="Last Updated">{{stocktake.updatedAt | formatDate}}</OverviewBoxListItem>
          </OverviewBoxList>
        </OverviewBox>

        <OverviewActionBox>
          <OverviewActionButton class="btn-default" @click="reviewStocktake" icon="fa-arrow-square-right" v-if="isCountingMode && userHasUpdatePermission(PermissionTypes.InventoryPermissionTypes.stockTake)">Move to Review</OverviewActionButton>
          <OverviewActionButton class="btn-default" @click="moveStocktakeCounting" icon="fa-arrow-square-left" v-if="(isStatusReview || isStatusRecount) && userHasUpdatePermission(PermissionTypes.InventoryPermissionTypes.stockTake)">Move back to Counting</OverviewActionButton>
          <OverviewActionButton class="btn-default" @click="recountStocktake" icon="fa-calculator" v-if="isStatusReview && userHasUpdatePermission(PermissionTypes.InventoryPermissionTypes.stockTake)">Move to Recount</OverviewActionButton>
          <OverviewActionButton class="btn-success" @click="completeStocktake" icon="fa-check" v-if="isStatusReview && userHasUpdatePermission(PermissionTypes.InventoryPermissionTypes.stockTakeApproval)">Complete Stocktake</OverviewActionButton>
          <OverviewActionButton class="btn-warning" @click="cancelStocktake" icon="fa-times" v-if="!isStatusCompleted && !isStatusCancelled && userHasUpdatePermission(PermissionTypes.InventoryPermissionTypes.stockTake)">Cancel Stocktake</OverviewActionButton>
        </OverviewActionBox>
      </div>
      <div class="col-sm-10">
        <TabbedBox :loading="loading">
          <template v-slot:tablist>
            <Tab :first="true" tabid="tab-1">
              <i class="far fa-warehouse-alt"></i> Stocktake
            </Tab>
            <Tab tabid="tab-2">
              <i class="far fa-history"></i> Activity Log
            </Tab>
          </template>
          <template v-slot:tabcontent>
            <TabPage :first="true" tabid="tab-1">
              <DataPageArea title="Details">
                <template slot="actionArea">
                  <ActionDropMenu v-if="!isStatusCancelled && !editingStockCounts" class="float-right">
                    <DropdownMenuItem @click="displayStockCountSheetReport" icon="fa-print" v-if="isCountingMode && (userHasReadPermission(PermissionTypes.InventoryPermissionTypes.stockTake) || userHasReadPermission(PermissionTypes.InventoryPermissionTypes.stockTakeCount))">Stock Count Sheet</DropdownMenuItem>
                    <DropdownMenuItem @click="displayManualEntry" icon="fa-scanner" v-if="isCountingMode && (userHasReadPermission(PermissionTypes.InventoryPermissionTypes.stockTake) || userHasReadPermission(PermissionTypes.InventoryPermissionTypes.stockTakeCount))">Manual Scan Entry</DropdownMenuItem>
                    <DropdownMenuItem @click="displayStockSummaryReport()" icon="fa-print" v-if="isStatusCompleted">Summary Report</DropdownMenuItem>
                    <DropdownMenuItem @click="displayStockSummaryReport({ hideStockValues: true })" icon="fa-print" v-if="isStatusCompleted">Summary Report (No stock values)</DropdownMenuItem>
                  </ActionDropMenu>
                </template>
                <template slot="dataArea">
                  <DataItem title="Name">{{stocktake.name}}</DataItem>
                  <DataItem title="Date Reviewed" v-if="stocktake.reviewDate">{{stocktake.reviewDate | formatDateTime}}</DataItem>
                  <DataItem title="Date Completed" v-if="isStatusCompleted">{{stocktake.endDate | formatDateTime}}</DataItem>
                  <DataItem title="Date Cancelled" v-if="isStatusCancelled">{{stocktake.endDate | formatDateTime}}</DataItem>
                </template>
              </DataPageArea>

              <FormGroup :form="stockCountForm" v-if="isStockCountsAccessible">
                <ul class="nav nav-tabs" role="tablist">
                  <Tab :first="true" tabid="tab-list-stock-1">
                    <i class="far fa-container-storage"></i> Glass
                  </Tab>
                  <Tab tabid="tab-list-stock-2">
                    <i class="far fa-box-up"></i> General
                  </Tab>
                </ul>

                <div class="tab-content">
                  <TabPage :first="true" tabid="tab-list-stock-1">
                    <div>
                      <DataTable ref="glassStockItemList" :columns="glassLevelColumnList" :url="glassStockURL" :filterOptions="filterOptions" :emptyListMessage="emptyStockListMessage">
                        <template v-slot:title>
                          <h3>Stock List</h3>
                        </template>
                        <template v-slot:tableHeader>
                          <DataTableTopHeaderRow>
                            <DataTableHeaderCell colspan="4"></DataTableHeaderCell>
                            <DataTableHeaderCell colspan="3" v-if="!isCountingMode">On Hand</DataTableHeaderCell>
                            <DataTableHeaderCell colspan="3" v-if="isStatusRecount">Previous Count</DataTableHeaderCell>
                            <DataTableHeaderCell colspan="3">Counted</DataTableHeaderCell>
                            <DataTableHeaderCell colspan="1" v-if="!isCountingMode"></DataTableHeaderCell>
                          </DataTableTopHeaderRow>
                        </template>
                        <template v-slot:row="row">
                          <tr :key="row.data.id">
                            <td class="table-data-nav-link" width="6%">
                              <LinkStock :name="row.data.code" :id="row.data.stockId" />
                            </td>
                            <td width="10%">{{row.data.stockCategoryName}}</td>
                            <td width="6%" class="text-center">{{row.data.height}}</td>
                            <td width="6%" class="text-center">{{row.data.width}}</td>
                            <template v-if="isStatusRecount">
                              <td width="6%" class="text-center">{{row.data.prevQuantity | formatInteger(false, true)}}</td>
                              <td width="6%" class="text-center">{{row.data.prevBlockQuantity | formatInteger(false, true)}}</td>
                              <td width="6%" class="text-center">{{row.data.totalPrevCountedSheets | formatInteger(false, true)}}</td>
                            </template>
                            <template v-if="!isCountingMode">
                              <td width="6%" class="text-center">{{row.data.onHandLooseSheets | formatInteger(false, true)}}</td>
                              <td width="6%" class="text-center">{{row.data.onHandBlocks | formatInteger(false, true)}}</td>
                              <td width="6%" class="text-center">{{row.data.onHandTotalSheets | formatInteger(false, true)}}</td>
                            </template>
                            <td width="6%" class="text-center">{{row.data.quantity | formatInteger(false, true)}}</td>
                            <td width="6%" class="text-center">{{row.data.blockQuantity | formatInteger(false, true)}}</td>
                            <td width="6%" class="text-center">{{row.data.totalCountedSheets | formatInteger(false, true)}}</td>
                            <template v-if="!isCountingMode">
                              <td width="6%" :class="{'text-center': true, 'financial-loss': row.data.sheetVariance < 0, 'financial-profit': row.data.sheetVariance > 0}">{{row.data.sheetVariance | formatInteger(false, true)}}</td>
                              <td width="10%" class="text-center">
                                <CurrencyValue :value="row.data.sheetVarianceAmount" highlightDifference />
                              </td>
                              <td width="6%" class="text-center">
                                <FormCheckbox tableInline v-if="editingStockCounts" :key="row.data.id" :value="isStocktakeItemRecount(row.data.id, row.data.isRecount)" @change="setStocktakeItemRecount(row.data.id, $event)" />
                                <IconBoolean :value="row.data.isRecount" :displayFalse="false" v-if="!editingStockCounts" />
                              </td>
                            </template>
                            <td width="2%">
                              <template v-if="row.data.hasErrors">
                                <Tooltip :label="row.data.errorDescription">
                                  <Icon iconStyle="fas" icon="fa-exclamation-triangle" iconClass="text-danger" iconStack="fa-circle" iconStackClass="text-white" class="mr-1"></Icon>
                                </Tooltip>
                              </template>
                            </td>
                            <td width="5%" v-if="canModifyStockCount">
                              <ActionDropMenu :tableAction="true">
                                <DropdownMenuItem @click="editStockCount(row.data.id)" icon="fa-pencil">Edit</DropdownMenuItem>
                              </ActionDropMenu>
                            </td>
                          </tr>
                        </template>
                        <template v-slot:summary="summary">
                          <tr key="summary">
                            <td>Totals</td>
                            <td></td>
                            <td></td>
                            <td></td>
                            <template v-if="!isCountingMode">
                              <td class="text-center">{{summary.data.totalLooseSheetsOnHand | formatInteger(false, true)}}</td>
                              <td class="text-center">{{summary.data.totalOnHandBlocks | formatInteger(false, true)}}</td>
                              <td class="text-center">{{summary.data.totalOnHandTotalSheets | formatInteger(false, true)}}</td>
                            </template>
                            <template v-if="isStatusRecount">
                              <td class="text-center">{{summary.data.totalPrevQuantity | formatInteger(false, true)}}</td>
                              <td class="text-center">{{summary.data.totalPrevBlockQuantity | formatInteger(false, true)}}</td>
                              <td class="text-center">{{summary.data.totalPrevTotalCountedSheets | formatInteger(false, true)}}</td>
                            </template>
                            <td class="text-center">{{summary.data.totalQuantity | formatInteger(false, true)}}</td>
                            <td class="text-center">{{summary.data.totalBlockQuantity | formatInteger(false, true)}}</td>
                            <td class="text-center">{{summary.data.totalTotalCountedSheets | formatInteger(false, true)}}</td>
                            <template v-if="!isCountingMode">
                              <td :class="{'text-center': true, 'financial-loss': summary.data.totalSheetVariance < 0, 'financial-profit': summary.data.totalSheetVariance > 0}">{{summary.data.totalSheetVariance | formatInteger(false, true)}}</td>
                              <td class="text-center"><CurrencyValue :value="summary.data.totalSheetVarianceAmount" highlightDifference /></td>
                              <td></td>
                            </template>
                            <td></td>
                            <td></td>
                          </tr>
                        </template>
                      </DataTable>
                    </div>
                  </TabPage>

                  <TabPage tabid="tab-list-stock-2">
                    <div>
                      <DataTable ref="generalStockItemList" v-bind:columns="generalStockColumnList" :url="generalStockURL" :filterOptions="filterOptions" :emptyListMessage="emptyStockListMessage">
                        <template v-slot:title>
                          <h3>Stock List</h3>
                        </template>
                        <template v-slot:row="row">
                          <tr :key="row.data.id">
                            <td class="table-data-nav-link" width="10%">
                              <LinkStock :name="row.data.stock.code" :id="row.data.stock.id" />
                            </td>
                            <td width="15%">{{row.data.stock.stockCategory.name}}</td>
                            <template v-if="!(isStatusCounting || isStatusRecount)">
                              <td width="5%" class="text-center">{{row.data.stock.quantity | formatDecimal(5, true)}}</td>
                            </template>
                            <template v-if="isStatusRecount">
                              <td width="5%" class="text-center">{{row.data.prevQuantity | formatDecimal(5, true)}}</td>
                            </template>
                            <td width="5%" class="text-center">{{row.data.quantity | formatDecimal(5, true)}}</td>
                            <template v-if="!(isStatusCounting || isStatusRecount)">
                              <td width="5%" :class="{'text-center': true, 'financial-loss': row.data.variance < 0, 'financial-profit': row.data.variance > 0}">{{row.data.variance}}</td>
                              <td width="10%" class="text-center"><CurrencyValue :value="row.data.varianceAmount" highlightDifference /></td>
                              <td width="10%" class="text-center">
                                <FormCheckbox tableInline v-if="editingStockCounts" :key="row.data.id" :value="isStocktakeItemRecount(row.data.id, row.data.isRecount)" @change="setStocktakeItemRecount(row.data.id, $event)" />
                                <IconBoolean :displayFalse="false" v-if="!editingStockCounts" :value="row.data.isRecount" />
                              </td>
                            </template>
                            <td width="2%">
                              <template v-if="row.data.hasErrors">
                                <Tooltip :label="row.data.errorDescription">
                                  <Icon iconStyle="fas" icon="fa-exclamation-triangle" iconClass="text-danger" iconStack="fa-circle" iconStackClass="text-white" class="mr-1"></Icon>
                                </Tooltip>
                              </template>
                            </td>
                            <td width="10%" v-if="canModifyStockCount">
                              <ActionDropMenu :tableAction="true">
                                <DropdownMenuItem @click="editStockCount(row.data.id)" icon="fa-pencil">Edit</DropdownMenuItem>
                              </ActionDropMenu>
                            </td>
                          </tr>
                        </template>
                        <template v-slot:summary="summary">
                          <tr key="summary">
                            <td>Totals</td>
                            <td></td>
                            <template v-if="!isCountingMode">
                              <td class="text-center">{{summary.data.totalQuantityOnHand | formatInteger(false, true)}}</td>
                            </template>
                            <template v-if="isStatusRecount">
                              <td class="text-center">{{summary.data.totalPrevQuantity | formatInteger(false, true)}}</td>
                            </template>
                            <td class="text-center">{{summary.data.totalQuantity | formatInteger(false, true)}}</td>
                            <template v-if="!isCountingMode">
                              <td :class="{'text-center': true, 'financial-loss': summary.data.totalVariance < 0, 'financial-profit': summary.data.totalVariance > 0}">{{summary.data.totalVariance | formatInteger(false, true)}}</td>
                              <td class="text-center"><CurrencyValue :value="summary.data.totalVarianceAmount" highlightDifference /></td>
                              <td></td>
                            </template>
                            <td></td>
                            <td></td>
                          </tr>
                        </template>
                      </DataTable>
                    </div>
                  </TabPage>
                </div>
              </FormGroup>
            </TabPage>
            <TabPage tabid="tab-2">
              <div class="row">
                <div class="col-lg-12">
                  <ActivityLogStream ref="activityStream" :url="activityStreamURL" :linkType="LinkTypes.stockTake" :linkId="stocktake.id">
                    <template v-slot:title><h3>Recent Activity</h3></template>
                  </ActivityLogStream>
                </div>
              </div>
            </TabPage>
          </template>
        </TabbedBox>
      </div>
    </div>
  </div>
</template>

<script>
import _ from 'lodash'
import Utils from '@/utils/utils'
import pdfMixin from '@/mixins/pdf'
import form from '@/mixins/form'

import StockTakeScanEntryDialog from '@/modules/inventory/views/stockTakeScanEntryDialog'

import { LinkTypes, StocktakeStatusTypes, StocktakeStatusTypeLookup, InventoryStockTypes } from '@/store/types'

export default {
  name: "StockTake",
  mixins: [form, pdfMixin],
  data() {
    return {
      loading: false,
      submittingCountUpdate: false,
      stockTakeId: null,
      stocktake: {},
      stockCountForm: {},
      stockCategoryList: [],
      stockSettingsList: {},
    }
  },
  computed: {
    isStatusCounting() {
      return this.stocktake.status == this.StocktakeStatusTypes.counting
    },
    isStatusRecount() {
      return this.stocktake.status == this.StocktakeStatusTypes.recount
    },
    isStatusReview() {
      return this.stocktake.status == this.StocktakeStatusTypes.review
    },
    isStatusCompleted() {
      return this.stocktake.status == this.StocktakeStatusTypes.completed
    },
    isStatusCancelled() {
      return this.stocktake.status == this.StocktakeStatusTypes.cancelled
    },
    isCountingMode() {
      return this.stocktake.status == this.StocktakeStatusTypes.counting || this.stocktake.status == this.StocktakeStatusTypes.recount
    },
    isStockCountsAccessible() {
      if(this.isStatusCompleted || this.isStatusCancelled)
        return false

      // If in review state, people with just stockTakeCount permission cannot access counts
      if(this.isStatusReview && !this.userHasReadPermission(this.PermissionTypes.InventoryPermissionTypes.stockTake))
        return false
      
      return true
    },
    editingStockCounts() {
      return this.isStatusReview && this.userHasUpdatePermission(this.PermissionTypes.InventoryPermissionTypes.stockTake)
    },
    hasStockRecountUpdates() {
      return Object.keys(this.$data.stockSettingsList).length > 0
    },

    emptyStockListMessage() {
      if(this.isStatusCounting)
        return 'No counted items. Start counting items to see the stocktake item list'
      if(this.isStatusRecount)
        return 'No available items available for recount or counted'

      return 'No stocktake items available'
    },

    glassStockCategoryList() {
      return this.$data.stockCategoryList.filter(stockCategory => stockCategory.stockType == InventoryStockTypes.glass)
    },
    generalStockCategoryList() {
      return this.$data.stockCategoryList.filter(stockCategory => stockCategory.stockType == InventoryStockTypes.general)
    },

    filterOptions() {
      if(this.isCountingMode)
        return null

      return [{
        name: 'Filters',
        options: [
          { name: 'Recount', value: 'isRecount', class: 'label-stocktake-status-recount' }
        ]
      }]
    },
    activityStreamURL() {
      return `/inventory/stocktakes/${this.$route.params.id}/activitystream`
    },

    glassStockURL() {
      if(this.stocktake.id == null)
        return ''
      var params = {
        filter: {}
      }

      // If in counting mode, we only display items flagged as counted
      if(this.isStatusCounting)
        params.filter.isCounted = 1
      if(this.isStatusRecount)
        params.filter.isRecount = 1

      return `/inventory/stocktakes/${this.$route.params.id}/glasslevels?` + $.param(params)
    },
    generalStockURL() {
      if(this.stocktake.id == null)
        return ''
      var params = {
        filter: {}
      }

      // If in counting mode, we only display items flagged as counted
      if(this.isStatusCounting)
        params.filter.isCounted = 1
      if(this.isStatusRecount)
        params.filter.isRecount = 1

      return `/inventory/stocktakes/${this.$route.params.id}/stock?` + $.param(params)
    },
    glassLevelColumnList() {
      if(this.isCountingMode) {
        return _.compact([
          { title: "Stock", data: "code" },
          { title: "Category", data: "stockCategoryName" },
          { title: "Height", data: "height", class: "text-center" },
          { title: "Width", data: "width", class: "text-center" },
          this.isStatusRecount ? { title: "Prev Sheets", data: "prevQuantity", class: "text-center" } : null,
          this.isStatusRecount ? { title: "Prev Blocks", data: "prevBlockQuantity", class: "text-center" } : null,
          this.isStatusRecount ? { title: "Prev Total", data: "totalPrevCountedSheets", class: "text-center" } : null,
          { title: "Sheets", data: "quantity", class: "text-center" },
          { title: "Blocks", data: "blockQuantity", class: "text-center" },
          { title: "Total", data: "totalCountedSheets", class: "text-center" },
          { title: "Info", data: "hasErrors", class: "text-center" },
          this.canModifyStockCount ? { title: "Actions", sortable: false } : null
        ])
      }

      return _.compact([
        { title: "Stock", data: "code" },
        { title: "Category", data: "stockCategoryName" },
        { title: "Height", data: "height", class: "text-center" },
        { title: "Width", data: "width", class: "text-center" },
        { title: "Loose Sheets", data: "onHandLooseSheets", class: "text-center" },
        { title: "Blocks", data: "onHandBlocks", class: "text-center" },
        { title: "Total", data: "onHandTotalSheets", class: "text-center" },
        { title: "Loose Sheets", data: "quantity", class: "text-center" },
        { title: "Blocks", data: "blockQuantity", class: "text-center" },
        { title: "Total", data: "totalCountedSheets", class: "text-center" },
        { title: 'Variance', data: 'sheetVariance', class: "text-center" },
        { title: 'Variance Amount', data: 'sheetVarianceAmount', class: "text-center" },
        { title: 'Recount', data: 'isRecount', sortable: !this.editingStockCounts, class: "text-center" },
        { title: "Info", data: "hasErrors", class: "text-center" },
        this.canModifyStockCount ? { title: "Actions", sortable: false } : null
      ])
    },
    generalStockColumnList() {
      if(this.isCountingMode) {
        return _.compact([
          { title: "Stock", data: "stock.code" },
          { title: "Category", data: "stock.stockCategory.name" },
          this.isStatusRecount ? { title: "Prev Count", data: "prevQuantity", class: "text-center" } : null,
          { title: "Counted", data: "quantity", class: "text-center" },
          { title: "Info", data: "hasErrors", class: "text-center" },
          this.canModifyStockCount ? { title: "Actions", sortable: false } : null
        ])
      }
      return _.compact([
        { title: "Stock", data: "stock.code" },
        { title: "Category", data: "stock.stockCategory.name" },
        { title: "On Hand", data: "stock.quantity", class: "text-center" },
        { title: "Counted", data: "quantity", class: "text-center" },
        { title: 'Variance', data: 'variance', class: "text-center" },
        { title: 'Variance Amount', data: 'varianceAmount', class: "text-center" },
        { title: 'Recount', data: 'isRecount', sortable: !this.editingStockCounts, class: "text-center" },
        { title: "Info", data: "hasErrors", class: "text-center" },
        this.canModifyStockCount ? { title: "Actions", sortable: false } : null
      ])
    },
    canModifyStockCount() {
      if(this.isStatusCompleted || this.isStatusCancelled)
        return false

      // Stocktake Approval permission can edit stock counts during count/recount/review
      if(this.userHasUpdatePermission(this.PermissionTypes.InventoryPermissionTypes.stockTakeApproval))
        return true

      // Stocktake permission can only edit stock counts during count/recount (limited to recount flagged items)
      if(this.userHasUpdatePermission(this.PermissionTypes.InventoryPermissionTypes.stockTake)) {
        if(this.isStatusCounting || this.isStatusRecount)
          return true
      }
      return false
    }
  },
  created() {
    this.LinkTypes = LinkTypes
    this.StocktakeStatusTypes = StocktakeStatusTypes
    this.StocktakeStatusTypeLookup = StocktakeStatusTypeLookup
  },
  mounted() {
    this.$data.stockTakeId = this.$route.params.id

    // Subscribe to all relevant endpoints
    this.subscribeEndpoint(`/inventory/stocktakes/${this.$data.stockTakeId}/itemcounts`, () => {
      this.refreshTables()
      this.refreshActivityStream()
    })

    this.requestData()
  },
  beforeRouteUpdate (to, from, next) {
    next()

    this.unsubscribeEndpoint(`/inventory/stocktakes/${this.$data.stockTakeId}/itemcounts`)
    this.$data.stockTakeId = this.$route.params.id

    this.subscribeEndpoint(`/inventory/stocktakes/${this.$data.stockTakeId}/itemcounts`, () => {
      this.refreshTables()
      this.refreshActivityStream()
    })

    this.requestData()
  },
  beforeDestroy() {
    this.unsubscribeEndpoint(`/inventory/stocktakes/${this.$data.stockTakeId}/itemcounts`)
  },
  methods: {
    reloadData() {
      this.$refs.activityStream.reloadData()
      this.requestData()
    },
    requestData() {
      this.loading = true

      Promise.all([
        this.$apiService.inventoryService.getStockTake(this.$route.params.id),
        this.$apiService.inventoryService.getStockCategoryList(),
      ])
      .then(results => {
        this.stocktake = results[0].data
        
        this.$data.stockCategoryList = results[1].data.filter(category => {
          return category.stockType == InventoryStockTypes.glass || category.stockType == InventoryStockTypes.general
        }).map(category => {
          return {
            id: category.id,
            name: category.name,
            stockType: category.stockType,
            expanded: false,
            isActive: category.isActive
          }
        })
      })
      .finally(() => {
        this.loading = false
      })
    },
    resetTables() {
      this.$refs.glassStockItemList.resetTableInfo()
      this.$refs.generalStockItemList.resetTableInfo()
    },
    refreshTables() {
      this.$refs.glassStockItemList.updateTableData()
      this.$refs.generalStockItemList.updateTableData()
    },
    refreshActivityStream() {
      this.$refs.activityStream.reloadData()
    },

    moveStocktakeCounting() {
      this.confirmMessage('Move Stocktake back to Counting?', 'Move the stocktake back to counting state? All stock will be available again for counting')
      .then(success => {
        if(success) {
          this.$apiService.inventoryService.updateStockTakeToCounting(parseInt(this.$route.params.id))
          .then(response => {
            this.successToast('Stocktake Updated')
            this.reloadData()
            this.resetTables()
            this.refreshTables()
          })
          .catch(err => {
            this.displayNetworkError(err, 'Error', 'Could not update stocktake')
          })
        }
      })
    },

    reviewStocktake() {
      this.confirmMessage('Move Stocktake into Review?', 'Move the stocktake into a review state? Counting will be paused during review')
      .then(success => {
        if(success) {
          this.$apiService.inventoryService.updateStockTakeToReview(parseInt(this.$route.params.id))
          .then(response => {
            this.successToast('Stocktake Updated')
            this.reloadData()
            this.resetTables()
            this.refreshTables()
          })
          .catch(err => {
            this.displayNetworkError(err, 'Error', 'Could not review stocktake')
          })
        }
      })
    },
    recountStocktake() {
      this.confirmMessage('Move Stocktake into Recount?', 'Move the stocktake into recount state? Only items flagged for recount will be available for counting')
      .then(success => {
        if(success) {
          var itemUpdateList = Object.keys(this.stockSettingsList).map(id => {
            return {
              id: parseInt(id),
              isRecount: this.stockSettingsList[id].isRecount
            }
          })
          
          this.$apiService.inventoryService.updateStockTakeToRecount(parseInt(this.$route.params.id), { itemList: itemUpdateList })
          .then(response => {
            this.successToast('Stocktake Updated')

            // Reset recount settings
            this.stockSettingsList = {}

            this.reloadData()
            this.resetTables()
            this.refreshTables()
          })
          .catch(err => {
            this.displayNetworkError(err, 'Error', 'Could not move stocktake into recount')
          })
        }
      })
    },
    completeStocktake() {
      this.confirmMessage('Complete Stocktake?', 'Complete the stocktake and update all stock quantities. This action cannot be undone')
      .then(success => {
        if(success) {
          this.$apiService.inventoryService.completeStockTake(parseInt(this.$route.params.id)).then(response => {
            this.successToast('Stocktake Completed')
            this.reloadData()
            this.resetTables()
            this.refreshTables()
          })
          .catch(err => {
            this.displayNetworkError(err, 'Error', 'Could not complete stocktake')
          })
        }
      })
    },
    cancelStocktake() {
      this.confirmMessage('Cancel Stocktake?', 'Cancel the stocktake? This action cannot be undone')
      .then(success => {
        if(success) {
          this.$apiService.inventoryService.cancelStockTake(parseInt(this.$route.params.id)).then(response => {
            this.successToast('Stocktake Cancelled')
            this.reloadData()
            this.resetTables()
            this.refreshTables()
          })
          .catch(err => {
            this.displayNetworkError(err, 'Error', 'Could not cancel stocktake')
          })
        }
      })
    },

    displayStockCountSheetReport() {
      this.displayReportPDF(`/inventory/stocktakes/${this.$route.params.id}/countingsheetreport`)
    },
    displayStockSummaryReport(options = {}) {
      var params = $.param(options)
      this.displayReportPDF(`/inventory/stocktakes/${this.$route.params.id}/summaryreport?${params}`)
    },

    editStockCount(stockTakeItemId) {
      this.displayModal(StockTakeScanEntryDialog, { 
        stockTakeId: parseInt(this.$route.params.id),
        stockTakeItemId: parseInt(stockTakeItemId)
      })
      .then(() => {
        this.refreshTables()
      })
    },

    displayManualEntry() {
      this.displayModal(StockTakeScanEntryDialog, { 
        stockTakeId: parseInt(this.$route.params.id),
      })
      .then(() => {
        this.refreshTables()
      })
    },

    categoryBarClicked(evt, category, nameColumn) {
      // Ignore clicks on switches
      if($(evt.target).is(':input') || $(evt.target).is('label'))
        return true

      category.expanded = !category.expanded
      return true
    },
    stockBarClicked(evt, stock, nameColumn) {
      // Ignore clicks on switches
      if($(evt.target).is(':input') || $(evt.target).is('label'))
        return true

      stock.expanded = !stock.expanded
      return true
    },

    // Stocktake Item recounts - Used for editing mode within the datatable
    isStocktakeItemRecount(stockTakeItemId, currentRecountValue) {
      if(this.stockSettingsList[stockTakeItemId] === undefined || this.stockSettingsList[stockTakeItemId].isRecount === undefined)
        return currentRecountValue

      return this.stockSettingsList[stockTakeItemId].isRecount
    },
    setStocktakeItemRecount(stockTakeItemId, recountValue) {
      if(this.stockSettingsList[stockTakeItemId] === undefined) {
        this.stockSettingsList[stockTakeItemId] = {}
      }
      this.stockSettingsList[stockTakeItemId].isRecount = recountValue
    },

    submitStocktakeCounts() {
      var itemUpdateList = Object.keys(this.stockSettingsList).map(id => {
        return {
          id: parseInt(id),
          isRecount: this.stockSettingsList[id].isRecount
        }
      })

      this.submittingCountUpdate = true
      this.stockCountForm = this.clearFormResponse()

      this.$apiService.inventoryService.updateStockTakeItems(this.$route.params.id, { itemList: itemUpdateList })
      .then(response => {
        this.refreshTables()
        this.successToast('Stocktake Items Updated')

        // Reset list
        this.stockSettingsList = {}
      })
      .catch(err => {
        this.stockCountForm = this.processFormResponse(err)
      })
      .finally(() => {
        this.submittingCountUpdate = false
      })      
    }
  }
}
</script>
